import { gql } from "lib/apolloClient";
import { rubricFragment } from "Rubric/modules/RubricFragments";
import { checklistFragment } from "Checklist/modules/ChecklistFragments";
import { singlePointRubricFragment } from "SinglePointRubric/modules/SinglePointRubricFragments";
import {
  userFragment,
  staffFragment,
  attachmentFragment,
  curriculumProgramFragment,
  subjectGroupFragment,
  attachmentGroupsFragment,
  fieldScoreFragment,
} from "modules/CommonFragments";
import {
  assessmentToolFragment,
  assessmentEvaluationFragment,
} from "modules/AssessmentTool/AssessmentToolFragment";
import {
  assessmentPlannerElementFragments,
  assessmentBenchmarkFragment,
} from "AppComponents/AssessmentLearningGoals/module/AssessmentLearningGoalsFragments";

/**
 * Assessment-specific version of subjectBenchmarkDetails that uses the assessment
 * benchmarkFragment (with gradingType, standardsWrapper, parentData) instead of
 * the common pypElementFragment.benchmark (without those fields).
 * This prevents fragment name collision for "benchmarkItem" in assessment queries.
 */
const assessmentSubjectBenchmarkFragment = gql`
  fragment subjectItem on Subject {
    id
    name
    icon
    grades {
      id
      name
    }
    isArchived
    benchmarkRootNode {
      ...benchmarkItem
    }
    globalSubject
    framework
    snsChildren
    standardSet {
      id
      title
      description
      code
    }
    subjectLevelLabels {
      cuLevelName
      strandLevelName
      loLevelName
    }
    type
    tags {
      id
      key
      value
    }
    variants {
      level {
        id
        type
        label
      }
      responseLanguages {
        id
        type
        label
      }
    }
    flatVariants(filters: { archivalState: ALL }) {
      isArchived
      level {
        id
        type
        label
      }
      responseLanguage {
        id
        type
        label
      }
    }
  }
  ${assessmentBenchmarkFragment.benchmark}
`;

export const assessmentSubjectFragment = gql`
  fragment assessmentSubjectItem on Subject {
    ...subjectItem
    subjectGroup {
      id
      name
      constants
      globalSubjectGroup {
        id
        name
        constants
      }
    }
    aiClassification {
      id
      type
      label
    }
  }
  ${assessmentSubjectBenchmarkFragment}
`;

const assessmentResolvedFieldValueFragment = gql`
  fragment assessmentResolvedFieldValueItem on ResolvedFieldValue {
    id
    type
    ... on ResolvedFieldBenchmarkSet {
      id
      benchmarks {
        ...benchmarkItem
      }
    }
    ... on ResolvedFieldConceptSet {
      id
      concepts {
        ...conceptItem
      }
    }
    ... on ResolvedFieldActionSet {
      id
      actions {
        ...actionItem
      }
    }
    ... on ResolvedFieldLPSet {
      id
      learnerProfiles {
        ...lpItem
      }
    }
    ... on ResolvedFieldThemeSet {
      id
      themes {
        ...themeItem
      }
    }
    ... on ResolvedFieldSubjectSet {
      id
      subjects {
        ...assessmentSubjectItem
      }
    }
    ... on ResolvedFieldATLSet {
      id
      atls {
        ...atlItem
      }
    }
    ... on ResolvedFieldRelatedConceptSet {
      id
      relatedConcepts {
        ...relatedConceptItem
      }
    }
    ... on ResolvedFieldVoiceInstruction {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldVideoAttachment {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldStudentTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldLtiTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldOpeningMessageResource {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldTags {
      id
      tags {
        id
        label
      }
    }
    ... on ResolvedFieldGenericTags {
      id
      genericTags {
        id
        label
        __typename
      }
      __typename
    }
    ... on ResolvedFieldPlannerElementSet {
      id
      type
      nodes {
        ...plannerElementNodeItem
      }
    }
    ... on ResolvedFieldSubjectGroup {
      id
      type
      subjectGroups {
        ...subjectGroupWithGlobalSubjectGroupItem
      }
    }
    ... on ResolvedFieldScore {
      ...fieldScoreItem
    }
    ... on ResolvedFieldObjectiveLevel {
      id
      nodes {
        id
        label
        associatedParents {
          id
        }
      }
    }
    ... on ResolvedFieldAttachments {
      id
      type
      attachments {
        ...attachmentItem
      }
    }
  }
`;

export const unitPlanFragment = {
  unitPlanDetails: gql`
    fragment unitPlanItem on UnitPlan {
      id
      title {
        id
        value
      }
      theme {
        id
        value
      }
      duration {
        id
        value
      }
      image {
        id
        value
      }
      createdBy {
        ...userItem
      }
      leLibrary: resourceLibrary(filters: $leLibraryFilters) {
        leCount
      }
      assessmentLibrary: resourceLibrary(filters: $assessmentLibraryFilters) {
        assessmentCount
      }
      collaborators: allCollaborators(filters: $collaboratorsFilter) {
        totalCount
        edges {
          permission
          node {
            ...userItem
          }
        }
      }
    }
    ${userFragment.basicUserDetails}
  `,
  unitPlanBasicDetails: gql`
    fragment unitPlanBasicDetailsItem on UnitPlan {
      id
      templateId
      grades
      title {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ... on ResolvedFieldVideoAttachment {
            id
            type
            attachment {
              id
              type
              url
              thumbUrl
            }
          }
        }
      }
      allFields {
        id
        uid
        value
        resolvedMinimalTree {
          ...assessmentResolvedFieldValueItem
        }
      }
      subjects {
        id
        value
      }
      resolvedGrades {
        id
        name
      }
      courses {
        id
      }
      organization {
        id
      }
    }
    ${subjectGroupFragment.subjectGroupWithGlobalSubjectGroup}
    ${assessmentPlannerElementFragments.plannerElementNode}
    ${assessmentBenchmarkFragment.benchmark}
    ${assessmentPlannerElementFragments.concept}
    ${assessmentPlannerElementFragments.action}
    ${assessmentPlannerElementFragments.lp}
    ${assessmentPlannerElementFragments.theme}
    ${assessmentPlannerElementFragments.atl}
    ${assessmentPlannerElementFragments.relatedConcept}
    ${assessmentSubjectBenchmarkFragment}
    ${attachmentFragment.attachment}
    ${attachmentGroupsFragment.attachmentGroup}
    ${fieldScoreFragment.fieldScore}
    ${assessmentResolvedFieldValueFragment}
    ${assessmentSubjectFragment}
  `,
};

export const resolvedFieldValueFragment = {
  resolvedMinimalTree: gql`
    fragment resolvedMinimalTreeItem on ResolvedFieldValue {
      ... on ResolvedFieldUnitPlan {
        id
        type
        unitPlan {
          ...unitPlanBasicDetailsItem
        }
      }
      ...assessmentResolvedFieldValueItem
    }

    ${assessmentPlannerElementFragments.plannerElementNode}
    ${assessmentBenchmarkFragment.benchmark}
    ${assessmentPlannerElementFragments.concept}
    ${assessmentPlannerElementFragments.action}
    ${assessmentPlannerElementFragments.lp}
    ${assessmentPlannerElementFragments.theme}
    ${assessmentPlannerElementFragments.atl}
    ${assessmentPlannerElementFragments.relatedConcept}
    ${attachmentFragment.attachment}
    ${attachmentGroupsFragment.attachmentGroup}
    ${fieldScoreFragment.fieldScore}
    ${unitPlanFragment.unitPlanBasicDetails}
    ${assessmentResolvedFieldValueFragment}
    ${assessmentSubjectFragment}
  `,
  resolvedFieldVideoAttachment: gql`
    fragment resolvedFieldVideoAttachmentItem on ResolvedFieldVideoAttachment {
      id
      type
      attachment {
        id
        type
        url
        thumbUrl
      }
    }
  `,
};

export const plannerFieldFragment = {
  resourceLibraryField: gql`
    fragment resourceLibraryFieldItem on PlannerField {
      id
      uid
      value
      resolvedMinimalTree {
        ...resolvedMinimalTreeItem
      }
    }
    ${resolvedFieldValueFragment.resolvedMinimalTree}
  `,
};

export const assessmentUnitPlanFragment = {
  unitPlanBasicDetails: gql`
    fragment unitPlanDetailsItem on UnitPlan {
      id
      title {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ... on ResolvedFieldVideoAttachment {
            id
            type
            attachment {
              id
              type
              url
              thumbUrl
            }
          }
        }
      }
      subjects {
        id
        value
      }
      resolvedGrades {
        id
        name
      }
      templateId
      grades
      collaborators: allCollaborators(filters: $collaboratorsFilter) {
        totalCount
        edges {
          permission
          node {
            id
            firstName
            middleName
            lastName
            profileImage
            email
            roles
            type
            role
            isArchived
            courses {
              id
              title
            }
          }
        }
      }
      allFields {
        id
        uid
        value
        resolvedMinimalTree {
          ...assessmentResolvedFieldValueItem
        }
      }
      fields(
        uids: [
          "title"
          "theme"
          "centralIdea"
          "image"
          "subjects"
          "relatedConcepts"
          "unitType"
          "lois"
          "lp"
          "atls"
          "goalList"
          "concepts"
          "benchmarks"
          "tags"
          "action"
        ]
      ) {
        id
        uid
        value
        resolvedMinimalTree {
          ...assessmentResolvedFieldValueItem
        }
      }
      courses {
        id
        __typename
      }
      organization {
        id
      }
    }
    ${subjectGroupFragment.subjectGroupWithGlobalSubjectGroup}
    ${assessmentPlannerElementFragments.plannerElementNode}
    ${assessmentBenchmarkFragment.benchmark}
    ${assessmentPlannerElementFragments.concept}
    ${assessmentPlannerElementFragments.action}
    ${assessmentPlannerElementFragments.lp}
    ${assessmentPlannerElementFragments.theme}
    ${assessmentPlannerElementFragments.atl}
    ${assessmentPlannerElementFragments.relatedConcept}
    ${assessmentSubjectBenchmarkFragment}
    ${attachmentFragment.attachment}
    ${attachmentGroupsFragment.attachmentGroup}
    ${fieldScoreFragment.fieldScore}
    ${assessmentResolvedFieldValueFragment}
  `,
};

export const assessmentFragment = {
  assessmentDetails: gql`
    fragment assessmentItem on Assessment {
      id
      type
      isLibraryPremium
      showLibraryPremiumBannerForUser(filters: $libraryBannerFilters)
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      title {
        id
        value
      }
      assessmentType {
        id
        value
      }
      taskType {
        id
        type
        label
      }
      createdBy {
        ...staffItem
      }
      image {
        id
        value
      }
      duration {
        id
        value
      }
      templateId
      assessmentTools {
        edges {
          node {
            id
            assessmentToolItem {
              ...assessmentEvaluationItem
            }
            assessmentToolType
            isTeacherEvaluationEnable
            isStudentEvaluationEnable
          }
        }
      }
      grades {
        id
        name
      }
      allFields {
        ...resourceLibraryFieldItem
      }
      organizationResources {
        id
        type
      }
      unitPlan {
        ...unitPlanDetailsItem
      }
      comments {
        totalCount
        unreadCount
      }
      communityComments {
        totalCount
      }
      isDeleted
      resources {
        totalCount
      }
      isImported
      enabledSections
      submittedToCommunity
      originalCreatedBy {
        id
        firstName
        lastName
        profileImage
        ... on Staff {
          schoolTenures(currentSchool: true) {
            id
            school {
              id
              name
            }
          }
        }
      }
      category {
        id
        label
        colorCode
        set {
          id
          associatedEntities {
            id
            ... on Course {
              id
              title
            }
            ... on Subject {
              id
              name
            }
            ... on Grade {
              id
              name
            }
          }
        }
      }
      globalCategories {
        id
        name
      }
      academicTerm {
        id
        label
        lockingStatus
      }
      weightage
      isLearningGoalsEvaluationEnabled
      aiConversation {
        edges {
          node {
            id
            entityType
            entitySubType
            metadata
            attachments {
              id
            }
            messages {
              edges {
                node {
                  id
                  authorType
                  content
                }
              }
            }
            analysisToolEvaluation(filters: { userFilter: ALL }) {
              id
              toolResponses {
                id
              }
            }
          }
        }
      }
      libraryGrades {
        id
        type
        label
      }
      librarySubjects {
        id
        type
        label
      }
      isWorksheetExtractionInProgress
      metaData
      learningCourse {
        id
      }
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${staffFragment.basicStaffDetails}
    ${plannerFieldFragment.resourceLibraryField}
    ${attachmentFragment.attachment}
    ${resolvedFieldValueFragment.resolvedMinimalTree}
    ${assessmentEvaluationFragment.assessmentEvaluation}
    ${assessmentUnitPlanFragment.unitPlanBasicDetails}
  `,
  assessmentPreviewDetails: gql`
    fragment assessmentItem on Assessment {
      id
      type
      isLibraryPremium
      showLibraryPremiumBannerForUser
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      title {
        id
        value
      }
      assessmentType {
        id
        value
      }
      taskType {
        id
        type
        label
      }
      createdBy {
        isArchived
        isArchivedV2(filters: $userArchivedFilters)
        ...staffItem
      }
      image {
        id
        value
      }
      duration {
        id
        value
      }
      templateId
      assessmentTools {
        edges {
          node {
            id
            assessmentToolItem {
              ...assessmentEvaluationItem
            }
            assessmentToolType
            isTeacherEvaluationEnable
            isStudentEvaluationEnable
          }
        }
      }
      grades {
        id
        name
      }
      allFields {
        ...resourceLibraryFieldItem
      }
      organizationResources {
        id
        type
      }
      unitPlan {
        ...unitPlanDetailsItem
      }
      comments {
        totalCount
        unreadCount
      }
      communityComments {
        totalCount
      }
      isDeleted
      resources {
        totalCount
      }
      isImported
      enabledSections
      submittedToCommunity
      originalCreatedBy {
        id
        firstName
        lastName
        profileImage
        ... on Staff {
          schoolTenures(currentSchool: true) {
            id
            school {
              id
              name
            }
          }
        }
      }
      category {
        id
        label
        colorCode
        set {
          id
          associatedEntities {
            id
            ... on Course {
              id
              title
            }
            ... on Subject {
              id
              name
            }
            ... on Grade {
              id
              name
            }
          }
        }
      }
      academicTerm {
        id
        label
      }
      weightage
      isLearningGoalsEvaluationEnabled
      aiConversation {
        edges {
          node {
            id
            entityType
            entitySubType
          }
        }
      }
      libraryGrades {
        id
        type
        label
      }
      librarySubjects {
        id
        type
        label
      }
      gradingConfigurations {
        id
        settings {
          id
          uid
          value
        }
      }
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${staffFragment.basicStaffDetails}
    ${plannerFieldFragment.resourceLibraryField}
    ${attachmentFragment.attachment}
    ${resolvedFieldValueFragment.resolvedMinimalTree}
    ${assessmentEvaluationFragment.assessmentEvaluation}
    ${assessmentUnitPlanFragment.unitPlanBasicDetails}
  `,
  assessmentSchedulerCard: gql`
    fragment assessmentItem on Assessment {
      id
      templateId
      title {
        id
        value
      }
      description {
        id
        value
      }
      createdBy {
        ...staffItem
      }
      image {
        id
        value
      }
      assessmentType {
        id
        value
      }
      duration {
        id
        value
      }
      fields(uids: ["evaluationType", "assessmentType"]) {
        id
        uid
        value
      }
      assessmentTools {
        edges {
          node {
            id
            assessmentToolType
          }
        }
      }
    }
    ${staffFragment.basicStaffDetails}
  `,
  assessmentFeed: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      assessmentType {
        id
        value
      }
      description {
        id
        value
      }
      createdBy {
        ...staffItem
      }
      image {
        id
        value
      }
    }
    ${staffFragment.basicStaffDetails}
  `,
  assessmentTool: gql`
    fragment assessmentItem on Assessment {
      id
      assessmentTool {
        id
        ... on Rubric {
          ...rubricItem
        }
        ... on Checklist {
          ...checklist
        }
        ... on Anecdote {
          ...anecdoteItem
        }
        ... on Exemplar {
          ...exemplarItem
        }
        ... on SinglePointRubric {
          ...singlePointRubricItem
        }
      }
    }
    ${assessmentToolFragment.exemplar}
    ${assessmentToolFragment.anecdote}
    ${checklistFragment.checklist}
    ${rubricFragment.rubric}
    ${singlePointRubricFragment.singlePointRubric}
  `,
  assessmentEvaluationFeed: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      templateId
      assessmentType {
        id
        value
      }
      image {
        id
        value
      }
      unitPlan {
        id
        title {
          id
          value
        }
      }
      fields(uids: ["duration", "size", "measureAssessing", "evaluationType"]) {
        id
        uid
        value
      }
    }
  `,
  studentPortfolioAssessmentEvaluationFeed: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      templateId
      assessmentType {
        id
        value
      }
      assessmentTool {
        id
        ... on Rubric {
          ...rubricItem
        }
        ... on Checklist {
          ...checklist
        }
        ... on Anecdote {
          ...anecdoteItem
        }
        ... on Exemplar {
          ...exemplarItem
        }
        ... on SinglePointRubric {
          ...singlePointRubricItem
        }
      }
      fields(uids: ["duration", "size", "measureAssessing", "evaluationType"]) {
        id
        uid
        value
      }
    }
    ${rubricFragment.rubric}
    ${checklistFragment.checklist}
    ${singlePointRubricFragment.singlePointRubric}
    ${assessmentToolFragment.anecdote}
    ${assessmentToolFragment.exemplar}
  `,
  assessmentEvaluationDetails: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      assessmentType {
        id
        value
      }
      description {
        id
        value
      }
      createdBy {
        ...staffItem
      }
      image {
        id
        value
      }
      duration {
        id
        value
      }
      templateId
      assessmentTool {
        id
        ... on Rubric {
          ...rubricItem
        }
        ... on Checklist {
          ...checklist
        }
        ... on Anecdote {
          ...anecdoteItem
        }
        ... on Exemplar {
          ...exemplarItem
        }
        ... on SinglePointRubric {
          ...singlePointRubricItem
        }
      }
      allFields {
        id
        uid
        value
        resolvedMinimalTree {
          ...resolvedMinimalTreeItem
        }
      }
      unitPlan {
        id
        title {
          id
          value
        }
        image {
          id
          value
          resolvedMinimalTree {
            ...resolvedFieldVideoAttachmentItem
          }
        }
        unitType {
          id
          value
        }
        grades
        templateId
        fields(uids: ["lois", "atls", "subjects", "lp", "benchmarks"]) {
          id
          uid
          value
          resolvedMinimalTree {
            ...resolvedMinimalTreeItem
          }
        }
      }
    }

    ${rubricFragment.rubric}
    ${checklistFragment.checklist}
    ${singlePointRubricFragment.singlePointRubric}
    ${assessmentToolFragment.anecdote}
    ${assessmentToolFragment.exemplar}
    ${staffFragment.basicStaffDetails}
    ${resolvedFieldValueFragment.resolvedMinimalTree}
    ${resolvedFieldValueFragment.resolvedFieldVideoAttachment}
  `,
  assessmentEvaluationModalFeed: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      duration {
        id
        value
      }
      createdBy {
        id
        firstName
        middleName
        type
        lastName
      }
      createdAt
      templateId

      assessmentType {
        id
        value
      }
      evaluations(courseId: $courseId) {
        totalCount
      }
      fields(uids: ["duration", "size", "measureAssessing"]) {
        id
        uid
        value
      }
    }
  `,
  assessmentSelectModalFeed: gql`
    fragment assessmentItem on Assessment {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      duration {
        id
        value
      }
      createdBy {
        id
        firstName
        middleName
        type
        lastName
        profileImage
      }
      assessmentType {
        id
        value
      }
      taskType {
        id
        type
        label
        __typename
      }
      fields(uids: ["duration", "size", "measureAssessing"]) {
        id
        uid
        value
        resolvedMinimalTree {
          id
          ... on ResolvedFieldUndefinedEntity {
            undefinedEntity
          }
        }
      }
      assessmentTools {
        edges {
          node {
            id
            assessmentToolItem {
              id
              ... on ScoreAssessmentTool {
                id
                scoreToolData {
                  id
                  maxScore
                }
              }
            }
            assessmentToolType
          }
        }
      }
      organizationResourcesV2(
        first: null
        filters: { types: WORKSHEET_RESOURCE, resourceTypes: FOLDER }
      ) {
        resourceTypeBasedCount {
          totalCount
          questionStatusWiseCount {
            status
            count
          }
          questionTypeWiseCount {
            questionTypeUid
            count
          }
        }
      }
      grades {
        id
        name
      }
    }
  `,
};

const plannerElementSetItem = gql`
  fragment plannerElementSetItem on PlannerElementSet {
    id
    label
    type
    nodes {
      id
      label
      displaySequence
      code
      type
      subText
      children
      parent
      subType
      isLeaf
      depth
      code
      metadata
      updatedAt
      tags: genericTags {
        id
        label
        icon {
          id
          url
        }
      }
      associatedParents {
        id
        ... on Grade {
          id
          name
        }
      }
    }
    __typename
  }
`;

export const fieldListFragments = {
  templateFieldConnectionItem: gql`
    fragment templateFieldConnectionItem on TemplateFieldConnection {
      edges {
        node {
          id
          uid
          plannerElementSet(filters: $templateFieldPlannerElementSetFilters) {
            ...plannerElementSetItem
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
    ${plannerElementSetItem}
  `,
};

export const plannerElementSetFragment = {
  plannerElementSetWithNodes: gql`
    fragment plannerElementSetWithNodesItem on PlannerElementNodeSetWrapperV2 {
      ... on PlannerElementNodeSetWrapper {
        publishedData(filters: $filters) {
          edges {
            node {
              ...plannerElementNodeItem
            }
          }
        }
      }
    }
    ${assessmentPlannerElementFragments.plannerElementNode}
  `,
};

const pypFocusAreaItemFragment = gql`
  fragment pypFocusAreaItem on FocusArea {
    id
    label
  }
`;

export const pypFocusAreaFragment = {
  focusArea: pypFocusAreaItemFragment,
  focusAreaSet: gql`
    fragment pypFocusAreaSetItem on PlannerFocusAreaSetWrapper {
      publishedData(filters: $plannerElementNonAtlFilters) {
        edges {
          node {
            ...pypFocusAreaItem
          }
        }
      }
    }
    ${pypFocusAreaItemFragment}
  `,
};

export const pypElementFragment = {
  theme: gql`
    fragment pypThemeItem on PlannerTheme {
      id
      label
      descriptor
      metadata
      associatedChildren {
        id
        label
      }
      focusAreas {
        ...pypFocusAreaItem
      }
      icon
    }
    ${pypFocusAreaFragment.focusArea}
  `,
  lp: gql`
    fragment pypLpItem on PlannerLearnerProfile {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  concept: gql`
    fragment pypConceptItem on PlannerConcept {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  atl: gql`
    fragment pypAtlItem on PlannerATL {
      id
      label
      children
      parent
      levelId
      levels {
        id
        value
      }
      depth
      isLeaf
      displaySequence
      atlCategory {
        id
      }
      subText
      grades {
        id
        name
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
};

export const pypPlannerElementSetFragment = {
  benchmarkSet: gql`
    fragment PlannerBenchmarkSetItem on PlannerElementNodeSetWrapperV2 {
      ... on PlannerBenchmarkSetWrapper {
        publishedData(filters: $benchmarkNodeFilters) {
          edges {
            node {
              ...benchmarkItem
            }
          }
        }
      }
    }
    ${assessmentBenchmarkFragment.benchmark}
  `,
  lpSet: gql`
    fragment pypLpSetItem on PlannerLearnerProfileSetWrapper {
      publishedData(filters: $plannerElementNonAtlFilters) {
        edges {
          node {
            ...pypLpItem
          }
        }
      }
    }
    ${pypElementFragment.lp}
  `,
  conceptSet: gql`
    fragment pypConceptSetItem on PlannerConceptSetWrapper {
      publishedData(filters: $plannerElementNonAtlFilters) {
        edges {
          node {
            ...pypConceptItem
          }
        }
      }
    }
    ${pypElementFragment.concept}
  `,
  atlSet: gql`
    fragment pypAtlSetItem on PlannerATLSetWrapper {
      publishedData(filters: $plannerElementATLFilters) {
        edges {
          node {
            ...pypAtlItem
          }
        }
      }
    }
    ${pypElementFragment.atl}
  `,
  themeSet: gql`
    fragment pypThemeSetItem on PlannerThemeSetWrapper {
      publishedData(filters: $plannerElementNonAtlFilters) {
        edges {
          node {
            ...pypThemeItem
          }
        }
      }
    }
    ${pypElementFragment.theme}
  `,
  actionSet: gql`
    fragment pypActionSetItem on PlannerActionSetWrapper {
      publishedData(filters: $plannerElementNonAtlFilters) {
        edges {
          node {
            id
            label
            subText
            children
            parent
            levels {
              id
              value
            }
            levelId
          }
        }
      }
    }
  `,
};

export const genericPlannerElementSetFragment = {
  plannerElementSetWithNodes: gql`
    fragment pypPlannerElementSetWithNodesItem on PlannerElementNodeSetWrapperV2 {
      ... on PlannerThemeSetWrapper {
        ...pypThemeSetItem
      }
      ... on PlannerATLSetWrapper {
        ...pypAtlSetItem
      }
      ... on PlannerLearnerProfileSetWrapper {
        ...pypLpSetItem
      }
      ... on PlannerConceptSetWrapper {
        ...pypConceptSetItem
      }
      ... on PlannerActionSetWrapper {
        ...pypActionSetItem
      }
      ... on PlannerFocusAreaSetWrapper {
        ...pypFocusAreaSetItem
      }
    }
    ${pypPlannerElementSetFragment.themeSet}
    ${pypPlannerElementSetFragment.atlSet}
    ${pypPlannerElementSetFragment.lpSet}
    ${pypPlannerElementSetFragment.conceptSet}
    ${pypPlannerElementSetFragment.actionSet}
    ${pypFocusAreaFragment.focusAreaSet}
  `,
};
